import type { MobileAppBarProps } from '@paypoteam/page-components';
import {
    ChartLineUp,
    ClockCountdown,
    HandPointing,
    Percent,
    ShoppingBag,
    ThumbsUp,
} from '@phosphor-icons/react/dist/ssr';
import type { ImageProps } from 'next/image';

import type { IconsLinksListProps } from '@/components/molecules/IconsLinksList/IconsLinksList';
import type { InfoModalContentType } from '@/components/organisms/RegistrationForm/RegistrationForm.types';
import type { ButtonsSectionConfigType } from '@/components/sections/ButtonsSection/ButtonsSection.types';
import type { DataInNumbersSectionConfigType } from '@/components/sections/DataInNumbersSection/DataInNumbersSection.types';
import type { InfoSectionConfigType } from '@/components/sections/InfoSection/InfoSection.types';
import type { InlineTilesSectionProps } from '@/components/sections/InlineTilesSection';
import type { ItemsContainerSectionProps } from '@/components/sections/ItemsContainerSection';
import type { ShopCardsConfigType } from '@/components/sections/ShopCardsSection/ShopCardsSection.types';

import { env } from '@/env.mjs';

const staticUrl = env.NEXT_PUBLIC_STATIC_WWW_URL_PL;

type RegistartionErrorType = {
    errorText: string;
    errorTextModal: string;
};

export const MOBILE_APP_BAR_CONFIG: MobileAppBarProps<ImageProps> = {
    header: 'Aplikacja PayPo',
    image: {
        src:
            process.env.NODE_ENV === 'production'
                ? `${process.env.assetEnv}/images/logos/logo-squares.svg`
                : '/images/logos/logo-squares.svg',
    },
    rating: '4.9',
    text: '+100k pobrań',
    buttonText: 'Pobierz',
    platformLinks: {
        iosLink: 'https://apps.apple.com/pl/app/paypo/id1581411073?l=pl',
        androidLink:
            'https://play.google.com/store/apps/details?id=com.paypo.mobile&hl=pl&gl=US&pli=1',
    },
};

export const IMAGE_BUTTONS_CONFIG: IconsLinksListProps['iconsData'] = [
    {
        image: {
            src: '/images/icons/app-store-button.svg',
            alt: 'App Store',
        },
        href: 'https://apps.apple.com/pl/app/paypo/id1581411073?l=pl',
    },
    {
        image: {
            src: '/images/icons/google-play-button.svg',
            alt: 'Google Play',
        },
        href: 'https://play.google.com/store/apps/details?id=com.paypo.mobile',
    },
];

export const SECTION_APPLICATION_CONFIG: ItemsContainerSectionProps = {
    containerConfig: {
        flexOptions: {
            preset: 'hero',
        },
    },
    items: [
        {
            type: 'icon-pointer-list-content',
            config: {
                contentTextConfig: {
                    textConfig: [
                        {
                            type: 'heading2',
                            text: 'Płać z aplikacją PayPo',
                            className: 'heading-spaces',
                        },
                        {
                            type: 'paragraph',
                            size: 's',
                            text: '<b>Pobierz aplikację i płać wygodnie w sklepach online oraz stacjonarnych.</b> Aplikacja PayPo pozwala Ci dodatkowo:',
                            className: 'heading-spaces',
                        },
                    ],
                },
                listData: [
                    {
                        Icon: HandPointing,
                        iconProps: {
                            'aria-hidden': 'true',
                        },
                        content: {
                            title: '<b>Potwierdzać transakcje</b> tylko jednym przyciskiem',
                        },
                    },
                    {
                        Icon: ChartLineUp,
                        iconProps: {
                            'aria-hidden': 'true',
                        },
                        content: {
                            title: 'Na bieżąco <b>sprawdzać swój limit zakupowy</b>',
                        },
                    },
                    {
                        Icon: ClockCountdown,
                        iconProps: {
                            'aria-hidden': 'true',
                        },
                        content: {
                            title: '<b>Monitorować terminy spłat</b> i płacić zawsze na czas',
                        },
                    },
                ],
                iconsData: IMAGE_BUTTONS_CONFIG,
                cta: {
                    children: 'Dowiedz się więcej o aplikacji',
                    href: `/p/aplikacja-mobilna/`,
                    'data-test-id': 'application-section-link-mobile-app',
                    className: '!pt-0 md:!pt-0 mt-8 md:mt-10',
                },
            },
        },
        {
            type: 'image',
            config: {
                src: '/images/graphics/mobile-app-promo.png',
                alt: '',
                className: 'md:mb-8 lg:m-0',
            },
        },
    ],
};

export const SECTION_LAW_INFO_CONFIG: InfoSectionConfigType = {
    title: 'Jesteśmy Członkiem Związku Przedsiębiorstw Finansowych w Polsce. Więcej informacji o celach, zasadach oraz sposobie funkcjonowania ZPF znajdziesz na stronie',
    titleCta: {
        children: 'zpf.pl.',
        href: `https://zpf.pl/`,
        'data-test-id': 'section-law-title-link-zpf',
    },
    cta: {
        children: 'Dowiedz się więcej o członkostwie',
        href: `/p/zpf`,
        'data-test-id': 'section-law-internal-link-zpf',
    },
    images: [
        {
            id: 1,
            src: '/images/logos/logo-zpf.svg',
            alt: 'Logo ZPF',
            width: 247,
            height: 57,
            link: 'https://zpf.pl/',
            'data-test-id': 'section-law-img-external-link-zpf',
        },
        {
            id: 2,
            src: '/images/graphics/zpf-cert.png',
            alt: 'Certyfikat ZPF',
            width: 103,
            height: 149,
            link: `${staticUrl}/documents/ZPF-cert.pdf`,
            'data-test-id': 'section-law-img-pdf-zpf',
        },
    ],
};

export const SECTION_SHOP_CARD_CONFIG: ShopCardsConfigType = {
    title: 'Tutaj zapłacisz z PayPo',
    subtitle: 'Linki afiliacyjne',
    cta: {
        children: 'Zobacz wszystkie dostępne sklepy',
        href: `/p/dostepnesklepy`,
        dataTestId: 'section-partners-shops-link',
    },
};

export const REGISTRATION_MODAL_CONFIG: InfoModalContentType = {
    title: 'Dołącz do PayPo i zyskaj aż 30 dni na płatność',
    listData: [
        {
            Icon: ShoppingBag,
            content: {
                title: 'Kupujesz wygodnie i bezpiecznie w ponad 36 tys. sklepów',
            },
        },
        {
            Icon: ThumbsUp,
            content: {
                title: 'Dołącz do ponad 2 milionów zadowolonych klientów',
            },
        },
        {
            Icon: Percent,
            content: {
                title: 'Korzystaj z atrakcyjnych promocji i kodów rabatowych',
            },
        },
    ],
};

export const SECTION_REVIEWS_CONFIG: InlineTilesSectionProps = {
    headerContentText: {
        textConfig: [
            {
                type: 'heading2',
                text: 'Zobacz ponad 65 tys. pozytywnych opinii',
                className: 'heading-spaces text-center',
                'data-test-id': 'section-reviews-heading',
            },
        ],
    },
    containerConfig: {
        gridOptions: {
            columns: 3,
            gap: 'm',
        },
        swiperOptions: {
            swiperProps: {
                slidesPerView: 'auto',
                centerInsufficientSlides: true,
            },
            breakpoint: 'lg',
        },
        className: 'h-[360px] lg:h-full !overflow-visible',
        'data-test-id': 'section-reviews-container',
    },
    items: [
        {
            type: 'review-card',
            config: {
                rating: '5.0',
                name: 'Monika',
                content:
                    'Jestem bardzo zadowolona z płatności z PayPo. W każdej chwili, kiedy czegoś potrzebuję, mogę zrobić szybko i sprawnie zakupy, a potem w dogodnym terminie dokonać spłaty.',
            },
        },
        {
            type: 'review-card',
            config: {
                rating: '5.0',
                name: 'Magdalena',
                content:
                    'Rewelacja - jak dla mnie najszybszy i najwspanialszy sposób zapłaty za przesyłki, które mogę spokojnie obejrzeć, przetestować, przymierzyć czy sprawdzić w domu. Bez nerwów, pośpiechu i zbędnego zamrażania gotówki. A przy tym tak pewnie i bezpiecznie. POLECAM',
            },
        },
        {
            type: 'review-card',
            config: {
                rating: '5.0',
                name: 'Ala',
                content:
                    'Nigdy się nie zawiodłam na usłudze PayPo. Czytelne i jasne zasady. Dobry system powiadomień. Korzystam i będę korzystać. ',
            },
        },
    ],
    additionalText: [
        {
            type: 'paragraph',
            className: 'text-3xs text-center text-grey-600',
            text: 'Prezentowane opinie pochodzą z serwisu Opineo. Weryfikacja opinii realizowana jest zgodnie z Regulaminem dla użytkowników serwisu Opineo.pl',
        },
    ],
    cta: {
        children: 'Zobacz opinie na Opineo',
        href: 'https://www.opineo.pl/opinie/paypo-pl',
        'data-test-id': 'section-reviews-opineo-link',
        variant: 'secondary',
    },
};

export const SECTION_BUTTONS_CONFIG: ButtonsSectionConfigType = {
    textConfig: [
        {
            type: 'heading2',
            text: 'Pobierz aplikację PayPo',
            className: 'heading-spaces text-center',
        },
        {
            type: 'paragraph',
            size: 's',
            text: 'Przekonaj się sam, jak bezpiecznie i proste jest płacenie z aplikacją PayPo. <b>Pobierz ją już dziś.</b>',
            className: 'text-center',
        },
    ],
    imageButtons: [
        {
            image: {
                src: '/images/icons/app-store-button.svg',
                alt: 'App Store',
            },
            href: 'https://apps.apple.com/pl/app/paypo/id1581411073?l=pl',
        },
        {
            image: {
                src: '/images/icons/google-play-button.svg',
                alt: 'Google Play',
            },
            href: 'https://play.google.com/store/apps/details?id=com.paypo.mobile',
        },
    ],
};

export const SECTION_DATA_IN_NUMBERS_CONFIG: DataInNumbersSectionConfigType = {
    numbersData: [
        {
            id: 1,
            data: '36 tys.',
            description: 'dostępnych sklepów',
        },
        {
            id: 2,
            data: '2 mln',
            description: 'aktywnych klientów',
        },
        {
            id: 3,
            data: '65 tys.',
            description: 'pozytywnych recenzji',
        },
    ],
};

export const SOCIALS_LINKS = [
    {
        name: 'Facebook',
        image: {
            src: '/images/icons/facebook.svg',
            alt: 'Facebook',
            className: 'max-w-[32px] dark:invert',
        },
        href: 'https://www.facebook.com/paypopl/',
    },
    {
        name: 'LinkedIn',
        image: {
            src: '/images/icons/linkedin.svg',
            alt: 'Linkedin',
            className: 'max-w-[32px] dark:invert',
        },
        href: 'https://pl.linkedin.com/company/paypo',
    },
    {
        name: 'Instagram',
        image: {
            src: '/images/icons/instagram.svg',
            alt: 'Instagram',
            className: 'max-w-[32px] dark:invert',
        },
        href: 'https://www.instagram.com/paypo.pl/',
    },
];

export const HANDLE_REGISTRATION_ERROR: RegistartionErrorType = {
    errorText: `Nie udało się pobrać zgód, przejdź do <a class="text-red-800 underline" href="${env.NEXT_PUBLIC_PANEL_URL}/registration">Rejestracji</a>`,
    errorTextModal: 'Nie udało się pobrać zgód, odśwież stronę.',
};
